/**
 * The brand a **stranger** may see, fetched and remembered — the one thing every public surface in the
 * fleet needs and none of them should implement.
 *
 * ## What this replaces
 *
 * `store`, `subscribe` and `checkout` each draw a page for somebody with no account, and each needed the
 * same twenty-odd brand facts. None could reach the private API from a browser, so each one:
 *
 * 1. proxied the brand through its OWN Lambda (an in-network read on the request path),
 * 2. narrowed the answer with its own copy of an allowlist,
 * 3. resolved the font slugs against the catalog itself,
 * 4. and cached the result in its own `brandcache.ts` — in a cookie on the apex plus `localStorage`,
 *    with its own key, its own TTL and its own schema version.
 *
 * Every one of those four was the same code in three repos. Step 1 and 2 moved into `brand` itself
 * (`GET /v1/public`, `api/public.py`); steps 3 and 4 are this file.
 *
 * ## One tier: this origin's `localStorage` — no cookie (2026-10-04)
 *
 * Until 2026-10-04 the answer was ALSO written to an apex cookie, `x_brand`, so that crossing from
 * `store.<apex>` to `checkout.<apex>` painted without a request. It was the wrong store for it:
 * a cookie travels on EVERY request to EVERY host under the apex — every API call, every image,
 * every font — and this one had grown past 4 KB (quantumalex's encoded payload was 4,577 bytes, so
 * browsers were already silently refusing to store it). The fleet rule since then is that a cookie
 * carries the session and nothing else (m7 `docs/ARCHITECTURE.md` → *Cookies*). The cost is one
 * request on the first visit to each public app, which the CDN answers in milliseconds.
 *
 * {@link writePublicBrandCache} also EXPIRES any `x_brand` a visitor still holds from before.
 *
 * A consumer paints from the cache **before** its first request and the request still goes out; when
 * the answer differs, the page re-dresses and the cache is rewritten. So a palette edit reaches a
 * returning visitor on their next page load rather than at the TTL.
 *
 * **TTL: twelve hours.** A brand changes a few times a year, so freshness is not the constraint — the
 * constraint is that a stale palette must not outlive a visit by much, and that a payload written by
 * a version of this code we no longer understand must expire on its own. `VERSION` covers the shape.
 *
 * ## It must work with none of this available
 *
 * Private windows, blocked cookies, cleared storage, a full quota, a corrupt payload somebody pasted:
 * every read and every write is wrapped, a failure is a miss, and a miss is a page that fetches.
 */

import type { Brand } from './types';
import type { BrandFonts } from './theme';
import { apexDomain } from './runtime';

/** `https://brand.api.<apex>/v1/public` — the one open route on the Brand API. */
export function publicBrandUrl(apex: string = apexDomain()): string {
  return `https://brand.api.${apex}/v1/public`;
}

/**
 * The brand fields `GET /v1/public` answers — the tenant's names, palette, faces and legal links.
 *
 * Declared as a `Pick` of {@link Brand} rather than as a shape of its own, so the two cannot drift: a
 * field renamed on the record breaks this list at compile time, and a consumer that reaches for
 * `audience` gets an error instead of `undefined` at runtime. That field is deliberately unpublished —
 * `api/public.py` says why, at length.
 *
 * ⚠️ Every key is present on the wire, and any of them may be the empty string: an unsaved brand
 * answers its defaults rather than a 404, because a public page has to render for a tenant nobody has
 * configured yet.
 */
export type PublicBrandRecord = Pick<
  Brand,
  | 'title'
  | 'tagline'
  | 'description'
  | 'logo_url'
  | 'default_language'
  | 'support_email'
  | 'primary_color'
  | 'secondary_color'
  | 'background_color'
  | 'background_alt_color'
  | 'surface_color'
  | 'surface_alt_color'
  | 'text_color'
  | 'muted_color'
  | 'border_color'
  | 'success_color'
  | 'danger_color'
  | 'primary_font_family'
  | 'secondary_font_family'
  | 'mono_font_family'
  | 'shop_url'
  | 'privacy_url'
  | 'terms_url'
  | 'refund_url'
  | 'background_url'
  | 'login_video_url'
  | 'favicon_url'
  | 'touch_icon_url'
  | 'links'
>;

/**
 * What `GET /v1/public` answers: the record, and the faces resolved against the catalog.
 *
 * Two keys rather than one because the record stores font SLUGS and a slug is not usable in CSS — see
 * {@link brandFontFaceCss}. `fonts` is keyed by the brand's OWN role names; translating those into a
 * page's vocabulary (a display face, a body face) is a decision about a document, which is what
 * {@link PAGE_FONT_ALIASES} is for.
 *
 * `brand` is a `Partial`, because a payload may have been cached by an older version of this code.
 */
export interface PublicBrand {
  brand: Partial<PublicBrandRecord>;
  fonts: BrandFonts;
}

/**
 * The public answer, flattened: the record's fields with the resolved faces on `fonts`.
 *
 * One object rather than two, because every consumer wants both together — a masthead reads `title`
 * and the theme reads `primary_color` off the same thing. It satisfies {@link ThemeSource}, so it is
 * what `applyBrandTheme` takes, and it is the shape `store`, `subscribe` and `checkout` name as their
 * own brand type.
 */
export type PublicBrandTheme = Partial<PublicBrandRecord> & { fonts: BrandFonts };

/** The public answer as one flat object — see {@link PublicBrandTheme}. */
export function themeSource(answer: PublicBrand | null | undefined): PublicBrandTheme {
  return { ...(answer?.brand ?? {}), fonts: answer?.fonts ?? {} };
}

/** The apex cookie this cache USED to write (until 2026-10-04). Kept only so it can be expired. */
export const BRAND_COOKIE = 'x_brand';

/** The per-origin copy. Namespaced per app by the caller, because two origins may hold different TTLs. */
export const BRAND_STORAGE_KEY = 'brand.public';

/**
 * The schema of the cached payload. **Bump it whenever a field changes meaning.**
 *
 * A payload whose version this code does not recognise is ignored rather than read defensively: the
 * failure mode of guessing is a page painted in half a brand.
 *
 * ⚠️ Version 2, and 1 is deliberately not readable. Version 1 was the shape `store` and `subscribe`
 * wrote from their own `brandcache.ts` — a flat `colors` map plus a `fonts` ARRAY keyed by page role. A
 * reader of this file would have to keep a translation for a payload those apps no longer write, so the
 * bump is what retires it: an old cookie is a miss and the next request fixes it.
 */
export const BRAND_CACHE_VERSION = 2;

/** Twelve hours, in milliseconds — see the module docstring. */
export const BRAND_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

/** The cached payload: the public answer, plus when it was written and what wrote it. */
export interface CachedPublicBrand extends PublicBrand {
  v: number;
  /** When it was written, so a stale payload expires without anybody clearing it. */
  t: number;
}

/** Whether a payload is one this code wrote, understands, and still considers fresh. */
export function usablePublicBrand(value: unknown, now = Date.now()): value is CachedPublicBrand {
  if (!value || typeof value !== 'object') return false;
  const cached = value as Partial<CachedPublicBrand>;
  if (cached.v !== BRAND_CACHE_VERSION) return false;
  if (typeof cached.t !== 'number' || !Number.isFinite(cached.t)) return false;
  // A timestamp in the future is as broken as one in the past — a clock change, or a payload somebody
  // edited. Both are misses.
  if (now - cached.t > BRAND_CACHE_TTL_MS || cached.t > now + BRAND_CACHE_TTL_MS) return false;
  return Boolean(cached.brand) && typeof cached.brand === 'object';
}

/**
 * The cached public brand from this origin's `localStorage`, or `null`.
 *
 * `storageKey` is per app (`links.brand`, `store.brand`, …) because two origins may hold different
 * TTLs. A blocked store, a corrupt payload or an expired one is a miss, and a miss is a fetch.
 */
export function readPublicBrandCache({
  storageKey = BRAND_STORAGE_KEY,
  now = Date.now(),
}: { storageKey?: string; now?: number } = {}): CachedPublicBrand | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    return usablePublicBrand(value, now) ? value : null;
  } catch {
    return null;
  }
}

/** Write the cache, and expire the retired apex cookie. Never throws. */
export function writePublicBrandCache(
  answer: PublicBrand,
  { storageKey = BRAND_STORAGE_KEY }: { storageKey?: string } = {},
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ ...answer, v: BRAND_CACHE_VERSION, t: Date.now() }),
    );
  } catch {
    // Private window, or a full quota. The page is already painted; nothing depends on this.
  }
  expireBrandCookie();
}

/**
 * Delete the `x_brand` cookie an older bundle wrote on the apex — it rode on every request to every
 * fleet host. A no-op when there is none. Same `Domain` and `Path` it was written with, or the
 * browser treats it as a different cookie and keeps the old one.
 */
export function expireBrandCookie(): void {
  if (typeof document === 'undefined') return;
  try {
    if (!document.cookie.split(';').some((part) => part.trim().startsWith(`${BRAND_COOKIE}=`)))
      return;
    document.cookie = `${BRAND_COOKIE}=; Domain=.${apexDomain()}; Path=/; Max-Age=0; SameSite=Lax`;
  } catch {
    // Cookies blocked — then there is nothing to expire either.
  }
}

/**
 * Fetch the public brand. **No token, no axios, no interceptors.**
 *
 * `fetch` rather than the package's axios client, deliberately: this is one unauthenticated GET, and the
 * three things axios is here for — the `X-Auth` header, the 401-to-login interceptor, a configured base
 * url — are all things this call must NOT have. A public page that bounced a stranger to login because a
 * decorative fetch failed is the `resources` role-loop bug again.
 */
export async function fetchPublicBrand({
  apex = apexDomain(),
  signal,
}: { apex?: string; signal?: AbortSignal } = {}): Promise<PublicBrand> {
  const response = await fetch(publicBrandUrl(apex), {
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`brand answered ${response.status}`);
  const body = (await response.json()) as PublicBrand;
  return { brand: body.brand ?? {}, fonts: body.fonts ?? {} };
}

/**
 * The public brand, from the cache if it is fresh, otherwise from the network — and cached on the way.
 *
 * ⚠️ This is **not** paint-then-revalidate on its own: it answers once. A consumer that wants the first
 * frame painted reads {@link readPublicBrandCache} synchronously, paints, and *then* awaits this — which
 * is two lines in the consumer and the only shape that can paint before an await. Hiding that behind one
 * function would make the fast path impossible to express.
 */
export async function loadPublicBrand(
  options: { apex?: string; storageKey?: string; signal?: AbortSignal } = {},
): Promise<PublicBrand> {
  const cached = readPublicBrandCache(options);
  if (cached) return { brand: cached.brand, fonts: cached.fonts };
  const answer = await fetchPublicBrand(options);
  writePublicBrandCache(answer, options);
  return answer;
}

/**
 * The public brand as a react-query definition — `useQuery(publicBrandQuery('wall'))` — for a page
 * dressed in it (wall, settings, orders, notify, learn, help): painted from this origin's cache on
 * the first frame, then revalidated against `GET /v1/public`. `placeholderData` rather than
 * `initialData`, so the cache is SHOWN without being trusted and a palette edited this morning still
 * arrives on this load. A plain object, so this package still imports no React and no react-query.
 *
 * `app` names the cache (`<app>.brand`) and the key (`[app, 'brand']`): two origins keep two copies.
 */
export function publicBrandQuery(app: string) {
  const storageKey = `${app}.brand`;
  const cached = readPublicBrandCache({ storageKey });
  return {
    queryKey: [app, 'brand'] as const,
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<PublicBrandTheme> => {
      const answer = await fetchPublicBrand(signal ? { signal } : {});
      writePublicBrandCache(answer, { storageKey });
      return themeSource(answer);
    },
    placeholderData: (): PublicBrandTheme | undefined => (cached ? themeSource(cached) : undefined),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  };
}
